import { getSections, REVIEW_STATUS, STEP_IDS, STEP_RECOMMENDATIONS } from '../../constants/wizard';
import type { ApplicationContextValue, FeatureFlags } from '../../contexts/ApplicationContext';
import type { Answers, RentalListing, UserStatus } from '../../models/rental-application';
import { TermSetStatus } from '@zg-rentals/ts-capps-client';
import type { OverviewSectionType, OverviewStepType, OverviewType, WizardFieldType } from '../../models';
import { get } from 'lodash-es';

export function getOverviewFromApplication(applicationContext: ApplicationContextValue): OverviewType {
  const { application, listing, userStatuses, termSetStatus, featureFlags, requiresTrueUp } = applicationContext;
  const answers = application.answers as Answers;
  const rentalListing = listing.rentalListing as RentalListing;

  const recommendations = getRecommendations({ answers, userStatuses });
  const SECTIONS = getSections(featureFlags, application.role);
  const sections = SECTIONS.map((section) => {
    const stepsInSection = section.steps.map((step) => {
      const requiredFields = step.fields.filter((field) =>
        getIsFieldRequired({ field, answers, rentalListing, featureFlags }),
      );
      let hasIncompleteField = false;
      let hasCompleteField = false;
      requiredFields.forEach((field) => {
        if (field.isUserStatusComplete) {
          const isComplete = field.isUserStatusComplete(userStatuses, requiresTrueUp);
          if (isComplete) {
            hasCompleteField = true;
          }
          if (field.validationRule) {
            hasIncompleteField = hasIncompleteField || !field.validationRule({ answers, userStatuses, featureFlags });
          }
        } else if (field.id.includes('[]')) {
          // currentIncomes[] - translates to doing a forEach on answers.currentIncomes
          const baseFieldId = field.id.split('[')[0];
          const answerArray: Array<Answers> = get(answers, baseFieldId);
          if (answerArray && answerArray.length > 0) {
            answerArray.forEach((_, index) => {
              // It is possible for an answer in an array to be required based on the answer in that array
              // For example currentIncome[].manager.name is required only if currentIncome[].type == employment
              const requiredFieldForAnswer = getIsFieldRequired({
                field,
                answers,
                rentalListing,
                index,
                featureFlags,
              });
              const value = get(answers, field.id.replace('[]', `[${index}]`));
              if (requiredFieldForAnswer) {
                if (typeof value !== 'undefined' && value !== null) {
                  if (field.validationRule) {
                    hasCompleteField =
                      hasCompleteField || field.validationRule({ answers, userStatuses, featureFlags, index });
                    hasIncompleteField =
                      hasIncompleteField || !field.validationRule({ answers, userStatuses, featureFlags, index });
                  } else {
                    hasCompleteField = true;
                  }
                } else {
                  hasIncompleteField = true;
                }
              }
            });
          } else {
            hasIncompleteField = true;
          }
        } else {
          const value = get(answers, field.id);
          if (typeof value !== 'undefined' && value !== null) {
            if (field.validationRule) {
              hasCompleteField = hasCompleteField || field.validationRule({ answers, userStatuses, featureFlags });
              hasIncompleteField = hasIncompleteField || !field.validationRule({ answers, userStatuses, featureFlags });
            } else {
              hasCompleteField = true;
            }
          } else {
            hasIncompleteField = true;
          }
        }
      });

      // Income documents are optional but also need a validation rule for maximum amount per income source
      const nonRequiredFieldsWithValidation = step.fields.filter(
        (field) => !getIsFieldRequired({ field, answers, rentalListing, featureFlags }) && field.validationRule,
      );
      nonRequiredFieldsWithValidation.forEach((field) => {
        if (field.id.includes('[]')) {
          const baseFieldId = field.id.split('[')[0];
          const answerArray: Array<Answers> = get(answers, baseFieldId);
          if (answerArray && answerArray.length > 0) {
            answerArray.forEach((_, index) => {
              const isComplete =
                !getIsFieldVisible({ field, answers, rentalListing, index }) ||
                !field.validationRule ||
                field.validationRule({ answers, userStatuses, featureFlags, index });
              hasIncompleteField = hasIncompleteField || !isComplete;
            });
          }
        } else {
          const isComplete =
            !getIsFieldVisible({ field, answers, rentalListing }) ||
            !field.validationRule ||
            field.validationRule({ answers, userStatuses, featureFlags });
          hasIncompleteField = hasIncompleteField || !isComplete;
        }
      });

      let stepStatus = REVIEW_STATUS.NOT_STARTED;
      if (requiredFields.length === 0) {
        stepStatus = REVIEW_STATUS.COMPLETE;
      } else if (hasIncompleteField && hasCompleteField) {
        stepStatus = REVIEW_STATUS.INCOMPLETE;
      } else if (hasCompleteField) {
        stepStatus =
          step.id === STEP_IDS.ID_VERIFICATION && termSetStatus !== TermSetStatus.CURRENT
            ? REVIEW_STATUS.INCOMPLETE
            : REVIEW_STATUS.COMPLETE;
      }
      return { ...step, status: stepStatus, recommendation: recommendations[step.id] };
    });
    return {
      ...section,
      steps: stepsInSection,
      status: getSectionStatus(stepsInSection),
      recommendations: stepsInSection.filter((step) => !!step.recommendation).length,
    } as OverviewSectionType;
  });
  return { status: getApplicationStatus(sections), sections };
}

export function isFieldOptional(field: WizardFieldType, featureFlags: FeatureFlags): boolean {
  return typeof field.optional === 'function' ? field.optional(featureFlags) : !!field.optional;
}

function getIsFieldRequired({
  field,
  answers,
  rentalListing,
  index,
  featureFlags,
}: {
  field: WizardFieldType;
  answers: Answers;
  rentalListing: RentalListing;
  index?: number;
  featureFlags: FeatureFlags;
}) {
  return getIsFieldVisible({ field, answers, rentalListing, index }) && !isFieldOptional(field, featureFlags);
}

function getIsFieldVisible({
  field,
  answers,
  rentalListing,
  index,
}: {
  field: WizardFieldType;
  answers: Answers;
  rentalListing: RentalListing;
  index?: number;
}) {
  if (!field.visibilityRule) {
    return true;
  }
  const { dependentFieldId, dependentFieldValue, dependentLocation } = field.visibilityRule;
  if (dependentFieldId && dependentFieldValue) {
    if (dependentFieldId.includes('[]') && index === undefined) {
      // required only if dependent field array has length
      const baseDependentFieldId = dependentFieldId.split('[')[0];
      const answerArray: Array<Answers> = get(answers, baseDependentFieldId);
      return answerArray && answerArray.length > 0;
    }
    const exactDependentFieldId = index === undefined ? dependentFieldId : dependentFieldId.replace('[]', `[${index}]`);
    const dependentFieldAnswer = get(answers, exactDependentFieldId);
    return dependentFieldValue(dependentFieldAnswer);
  }
  return dependentLocation?.(rentalListing) ?? false;
}

function getApplicationStatus(sectionsInApplication: Array<OverviewSectionType>) {
  return sectionsInApplication.filter((section) => section.status === REVIEW_STATUS.COMPLETE).length ===
    sectionsInApplication.length
    ? REVIEW_STATUS.COMPLETE
    : REVIEW_STATUS.INCOMPLETE;
}

function getSectionStatus(stepsInSection: Array<OverviewStepType>) {
  if (stepsInSection.length === stepsInSection.filter((step) => step.status === REVIEW_STATUS.COMPLETE).length) {
    return REVIEW_STATUS.COMPLETE;
  }
  if (stepsInSection.length === stepsInSection.filter((step) => step.status === REVIEW_STATUS.NOT_STARTED).length) {
    return REVIEW_STATUS.NOT_STARTED;
  }
  return REVIEW_STATUS.INCOMPLETE;
}

export function getRecommendations({ answers, userStatuses }: { answers: Answers; userStatuses: Array<UserStatus> }) {
  return Object.fromEntries(
    Object.entries(STEP_RECOMMENDATIONS).filter(([_, recommendation]) =>
      recommendation.condition({ answers, userStatuses }),
    ),
  );
}

export function getIsAnswersComplete(applicationContext: ApplicationContextValue): boolean {
  const { status } = getOverviewFromApplication(applicationContext);
  return status === REVIEW_STATUS.COMPLETE;
}

export function getFirstIncompleteStep(applicationContext: ApplicationContextValue): OverviewStepType | undefined {
  const { sections } = getOverviewFromApplication(applicationContext);
  let firstIncompleteStep: OverviewStepType | undefined;
  sections.forEach((section) => {
    const incompleteStep = section.steps.find((step) =>
      [REVIEW_STATUS.INCOMPLETE, REVIEW_STATUS.NOT_STARTED].includes(step.status),
    );
    if (incompleteStep && !firstIncompleteStep) {
      firstIncompleteStep = incompleteStep;
    }
  });
  return firstIncompleteStep;
}

export function getNextIncompleteStep(applicationContext: ApplicationContextValue): OverviewStepType | undefined {
  const { sections } = getOverviewFromApplication(applicationContext);
  const incompleteSteps = sections.flatMap((section) =>
    section.steps.filter((step) => [REVIEW_STATUS.INCOMPLETE, REVIEW_STATUS.NOT_STARTED].includes(step.status)),
  );
  return incompleteSteps[1];
}
